import React, { useCallback, useEffect, useRef, useState } from 'react';
import { trackEvent } from '../../utils/analytics';
import useVideoEngine, { SPEED_OPTIONS } from './hooks/useVideoEngine';
import useCueMirror from './hooks/useCueMirror';
import useScrub from './hooks/useScrub';
import useMilestones from './hooks/useMilestones';
import { IS_PRERENDER, makeVideoRefAttacher } from './hooks/usePrerenderSafe';
import {
  PlayIcon, PauseIcon,
  VolumeHighIcon, VolumeLowIcon, VolumeMuteIcon,
  FullscreenIcon, FullscreenExitIcon,
  SeekBackIcon, SeekForwardIcon,
  CaptionsIcon,
} from './icons';
import './CCPlayer.css';

/**
 * CCPlayerTheater — the click-to-play "watch" preset of the unified CellCog
 * player (design doc: agent_docs/cel1157_tutorials_hub_design.md §5).
 * Surfaces: /tutorials video pages (phase A) + the full-screen MediaViewer
 * via a thin wrapper (phase B). The muted-autoplay hero behavior is a
 * SEPARATE preset (CCPlayerAmbient, phase C — deferred).
 *
 * Contract highlights:
 *  - Poster + big play affordance; first click plays WITH sound.
 *  - Captions ON by default via our cue-mirror treatment (never the browser's
 *    cue slab). ⚠️ The caption line's TYPE SPEC (size/weight/position) is
 *    Iris's — final values land with her v2 mock set (her GO note, Aug 12);
 *    .ccp__cue carries placeholder styling until then.
 *  - Full control bar, keyboard (space/k · j/l · m · c · f · < >), auto-hide
 *    while playing, feedback pulses, buffered bar + hover time tooltip.
 *  - `apiRef` imperative handle: { seekTo(t, {andPlay}) } — the transcript
 *    timestamp chips' seek+play contract.
 *  - `analyticsContext` ({ video, page, location }) arms video_progress
 *    milestones + seek events; omit it (MediaViewer) and nothing is tracked.
 *  - Prerender-safe: src never armed under the ReactSnap UA (poster-only
 *    snapshot), muted attribute serialized for real.
 */
const CCPlayerTheater = ({
  src,
  poster,
  captions,            // VTT URL; omit to hide the captions toggle
  title,               // aria-label for the <video>
  theme = 'dark',
  fit = false,         // false = fixed 16:9 stage (tutorial pages); true =
                       // shrink-wrap-and-center at intrinsic aspect (viewer)
  initialTimestamp = 0,
  autoPlay = false,    // MediaViewer contract; tutorial pages leave false
  disableKeyboard = false,
  onTimeUpdate,        // (currentTime) => void — MediaViewer contract
  analyticsContext = null,
  videoRef: externalVideoRef,
  apiRef,              // imperative: { seekTo }
  className = '',
}) => {
  const internalVideoRef = useRef(null);
  const videoRef = externalVideoRef || internalVideoRef;
  const containerRef = useRef(null);
  const hideTimeoutRef = useRef(null);
  const hasSetInitialTimestamp = useRef(false);
  const hasTriggeredAutoPlay = useRef(false);
  const feedbackKey = useRef(0);

  const [started, setStarted] = useState(autoPlay);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(true);
  const [isHoveringControls, setIsHoveringControls] = useState(false);
  const [cursorHidden, setCursorHidden] = useState(false);
  const [captionsOn, setCaptionsOn] = useState(true);
  const [hoverTime, setHoverTime] = useState(null);
  const [hoverPosition, setHoverPosition] = useState(0);
  const [feedbackIcon, setFeedbackIcon] = useState(null);
  const [retryKey, setRetryKey] = useState(0);

  const track = useCallback((event, extra = {}) => {
    if (analyticsContext) trackEvent(event, { ...analyticsContext, ...extra });
  }, [analyticsContext]);

  const { checkMilestones, resetMilestones } = useMilestones((m) => track(`video_progress_${m}`));

  const handleTimeUpdate = useCallback((t, d) => {
    if (onTimeUpdate) onTimeUpdate(t);
    checkMilestones(t, d);
  }, [onTimeUpdate, checkMilestones]);

  const engine = useVideoEngine(videoRef, { src, onTimeUpdate: handleTimeUpdate });
  const {
    playing, currentTime, duration, buffered, loading, error, volume, speed,
    togglePlay, seekTo, seekBy, setVolume, toggleMute, cycleSpeed, clearError,
  } = engine;

  const cue = useCueMirror(videoRef, Boolean(captions) && !IS_PRERENDER);

  const { trackRef, scrubbing, scrubHandlers } = useScrub(
    videoRef, duration, () => track('cta_click', { cta_name: 'video_seek' }),
  );

  // Reset per-source state — keyed on the SOURCE ONLY. `autoPlay` must NOT be
  // a dependency: consumers can recompute it live (the viewer's resume-position
  // feedback flips it mid-playback), and re-running this reset on a prop
  // wobble stomped `started` back to false over an actively playing video —
  // the recurring poster-button-over-playing-video bug. A changed autoPlay on
  // the SAME video is never a reason to reset; only a new video is.
  const autoPlayRef = useRef(autoPlay);
  autoPlayRef.current = autoPlay;
  useEffect(() => {
    resetMilestones();
    hasSetInitialTimestamp.current = false;
    hasTriggeredAutoPlay.current = false;
    setStarted(autoPlayRef.current);
  }, [src, resetMilestones]);

  // `started` must FOLLOW reality, not just the autoPlay prop: playback can
  // begin through paths the prop doesn't capture (the viewer's resume flow,
  // prop-settling races on mount). Whenever the engine reports actual
  // playback, the poster affordance is over — Nitish's catch: the big play
  // button sat over an already-playing video in the side panel.
  useEffect(() => {
    if (playing) setStarted(true);
  }, [playing]);

  // Imperative API (transcript chips).
  useEffect(() => {
    if (!apiRef) return undefined;
    apiRef.current = {
      seekTo: (t, opts = {}) => {
        setStarted(true);
        seekTo(t, { andPlay: opts.andPlay !== false });
      },
    };
    return () => { apiRef.current = null; };
  }, [apiRef, seekTo]);

  // Initial timestamp (MediaViewer contract).
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !(initialTimestamp > 0) || hasSetInitialTimestamp.current) return undefined;
    const apply = () => {
      if (!hasSetInitialTimestamp.current) {
        v.currentTime = initialTimestamp;
        hasSetInitialTimestamp.current = true;
      }
    };
    if (v.readyState >= 3) { apply(); return undefined; }
    v.addEventListener('canplay', apply, { once: true });
    return () => v.removeEventListener('canplay', apply);
  }, [initialTimestamp, videoRef, src]);

  // Auto-play (MediaViewer contract; never during prerender).
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !src || !autoPlay || IS_PRERENDER || hasTriggeredAutoPlay.current) return undefined;
    const delay = initialTimestamp > 0 ? 200 : 100;
    const timer = setTimeout(() => {
      if (!hasTriggeredAutoPlay.current) {
        hasTriggeredAutoPlay.current = true;
        v.play().catch(() => {});
      }
    }, delay);
    return () => clearTimeout(timer);
  }, [src, autoPlay, initialTimestamp, videoRef]);

  // ---- Controls visibility (auto-hide while playing) ----
  const showControls = useCallback(() => {
    setControlsVisible(true);
    setCursorHidden(false);
    clearTimeout(hideTimeoutRef.current);
    if (playing && !isHoveringControls) {
      hideTimeoutRef.current = setTimeout(() => {
        if (!scrubbing) {
          setControlsVisible(false);
          setCursorHidden(true);
        }
      }, 2500);
    }
  }, [playing, isHoveringControls, scrubbing]);

  useEffect(() => {
    if (!playing) {
      setControlsVisible(true);
      setCursorHidden(false);
      clearTimeout(hideTimeoutRef.current);
    } else {
      showControls();
    }
  }, [playing, showControls]);

  useEffect(() => () => clearTimeout(hideTimeoutRef.current), []);

  // ---- Feedback pulses ----
  const showFeedback = useCallback((type) => {
    feedbackKey.current += 1;
    setFeedbackIcon(type);
    setTimeout(() => setFeedbackIcon(null), 600);
  }, []);

  // ---- Surface interactions ----
  const startPlayback = useCallback(() => {
    setStarted(true);
    const v = videoRef.current;
    if (v) v.play().catch(() => {});
    track('cta_click', { cta_name: 'video_play' });
  }, [videoRef, track]);

  const handleSurfaceClick = useCallback((e) => {
    if (e.target.closest('.ccp__controls')) return;
    if (!started) { startPlayback(); return; }
    togglePlay();
    showFeedback(videoRef.current && videoRef.current.paused ? 'pause' : 'play');
  }, [started, startPlayback, togglePlay, showFeedback, videoRef]);

  const handleTouchEnd = useCallback((e) => {
    if (e.target.closest('.ccp__controls')) return;
    if (!started) return; // the poster button handles the first tap
    if (controlsVisible && playing) {
      setControlsVisible(false);
      setCursorHidden(true);
    } else if (!controlsVisible) {
      showControls();
    } else {
      togglePlay();
    }
  }, [started, controlsVisible, playing, showControls, togglePlay]);

  // ---- Fullscreen ----
  const toggleFullscreen = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    if (!isFullscreen) {
      if (container.requestFullscreen) container.requestFullscreen();
      else if (container.webkitRequestFullscreen) container.webkitRequestFullscreen();
    } else if (document.exitFullscreen) document.exitFullscreen();
    else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
  }, [isFullscreen]);

  useEffect(() => {
    const onChange = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onChange);
    document.addEventListener('webkitfullscreenchange', onChange);
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
      document.removeEventListener('webkitfullscreenchange', onChange);
    };
  }, []);

  // ---- Keyboard ----
  useEffect(() => {
    if (disableKeyboard) return undefined;
    const onKeyDown = (e) => {
      const tag = e.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target.isContentEditable) return;
      switch (e.key) {
        case ' ':
        case 'k':
        case 'K':
          e.preventDefault();
          if (!started) { startPlayback(); break; }
          togglePlay();
          showFeedback(videoRef.current && videoRef.current.paused ? 'pause' : 'play');
          break;
        case 'm':
        case 'M':
          e.preventDefault();
          toggleMute();
          break;
        case 'f':
        case 'F':
          e.preventDefault();
          toggleFullscreen();
          break;
        case 'c':
        case 'C':
          if (captions) { e.preventDefault(); setCaptionsOn((on) => !on); }
          break;
        case 'j':
        case 'J':
          e.preventDefault();
          seekBy(-10);
          showFeedback('seekBack');
          break;
        case 'l':
        case 'L':
          e.preventDefault();
          seekBy(10);
          showFeedback('seekForward');
          break;
        case '>':
          e.preventDefault();
          cycleSpeed(1);
          break;
        case '<':
          e.preventDefault();
          cycleSpeed(-1);
          break;
        default:
          break;
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [disableKeyboard, started, startPlayback, togglePlay, toggleMute, toggleFullscreen,
    seekBy, cycleSpeed, captions, showFeedback, videoRef]);

  // ---- Helpers ----
  const formatTime = (seconds) => {
    if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
    return `${Math.floor(seconds / 60)}:${String(Math.floor(seconds % 60)).padStart(2, '0')}`;
  };

  const handleProgressHover = useCallback((e) => {
    const bar = trackRef.current;
    if (!bar || !duration) return;
    const r = bar.getBoundingClientRect();
    const frac = Math.max(0, Math.min(1, (e.clientX - r.left) / r.width));
    setHoverTime(frac * duration);
    setHoverPosition(frac * 100);
  }, [trackRef, duration]);

  const handleRetry = useCallback(() => {
    clearError();
    setRetryKey((k) => k + 1);
  }, [clearError]);

  const attachVideoRef = makeVideoRefAttacher(videoRef, { mutedAttr: false });

  const progressPercent = duration ? (currentTime / duration) * 100 : 0;
  const bufferedPercent = duration ? (buffered / duration) * 100 : 0;
  const VolumeIcon = volume === 0 ? VolumeMuteIcon : volume < 0.5 ? VolumeLowIcon : VolumeHighIcon;

  // Never swap to the error UI during prerender: react-snap's bundled Chromium
  // has no h264 codec, so the armed <video src> ALWAYS fires an error event
  // there — which replaced the entire player with "Failed to load video" in the
  // static snapshot (discovered Aug 30: the first armed-src build shipped error
  // UI on all 21 tutorial pages). Real browsers decode fine; the snapshot must
  // keep the real <video> element for Google's watch-page detection.
  if (error && !IS_PRERENDER) {
    return (
      <div className={`ccp ccp--${theme} ccp--error-state ${className}`}>
        <div className="ccp__error">
          <p>Failed to load video</p>
          <button type="button" className="ccp__error-retry" onClick={handleRetry}>Try again</button>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={[
        'ccp', `ccp--${theme}`,
        fit ? 'ccp--fit' : '',
        isFullscreen ? 'is-fullscreen' : '',
        cursorHidden ? 'ccp--cursor-hidden' : '',
        started ? 'is-started' : 'is-poster',
        className,
      ].filter(Boolean).join(' ')}
      onMouseMove={showControls}
    >
      {/* The src IS serialized into the prerendered snapshot (Aug 30, video-indexing
          fix): Google's watch-page detection needs the static HTML to contain a real
          <video src> — a poster-only player reads as "no video here" and lands the
          page in GSC's "Video isn't on a watch page" bucket. This is safe: Theater's
          snapshot carries NO autoplay and NO controls attribute, so playback cannot
          start in the pre-React window at all — the HomeHeroVideo lesson (an armed
          AUTOPLAY video in a snapshot) targets autoplay surfaces like Ambient, where
          the serialized `muted` attribute is the guard. Theater stays mutedAttr:false
          (serializing muted here would flip defaultMuted for real visitors).
          preload="metadata" matches post-hydration behavior, so visitors see no
          change. */}
      <video
        key={retryKey}
        ref={attachVideoRef}
        className="ccp__video"
        src={src}
        poster={poster}
        preload="metadata"
        playsInline
        aria-label={title}
        onClick={handleSurfaceClick}
        onTouchEnd={handleTouchEnd}
      >
        {captions && !IS_PRERENDER && (
          /* No `default`: useCueMirror manages the mode; a default track can
             flash the native overlay before JS forces 'hidden'. */
          <track kind="captions" src={captions} srcLang="en" label="English" />
        )}
      </video>

      {/* Big play affordance until first play */}
      {!started && (
        <button type="button" className="ccp__bigplay" onClick={startPlayback} aria-label={`Play: ${title || 'video'}`}>
          <span className="ccp__bigplay-circle"><PlayIcon size={30} /></span>
        </button>
      )}

      {/* Caption overlay — OUR cue treatment, never the browser slab.
          ⚠️ Placeholder type spec: final size/weight/position comes with
          Iris's v2 mocks (her sign-off note). */}
      {started && captionsOn && cue && (
        <div className="ccp__cue" aria-hidden="true"><span>{cue}</span></div>
      )}

      {/* Loading spinner */}
      {started && loading && (
        <div className="ccp__loading"><div className="ccp__spinner" /></div>
      )}

      {/* Feedback pulse */}
      {feedbackIcon && (
        <div className="ccp__feedback" key={feedbackKey.current}>
          <div className="ccp__feedback-circle">
            {feedbackIcon === 'play' && <PlayIcon size={32} />}
            {feedbackIcon === 'pause' && <PauseIcon size={32} />}
            {feedbackIcon === 'seekBack' && <SeekBackIcon />}
            {feedbackIcon === 'seekForward' && <SeekForwardIcon />}
          </div>
        </div>
      )}

      {/* Control bar */}
      {started && (
        <div className={`ccp__controls ${controlsVisible ? 'is-visible' : 'is-hidden'}`}
          onMouseEnter={() => { setIsHoveringControls(true); clearTimeout(hideTimeoutRef.current); setControlsVisible(true); setCursorHidden(false); }}
          onMouseLeave={() => { setIsHoveringControls(false); if (playing) showControls(); }}
        >
          {/* Progress lane */}
          <div
            className={`ccp__progress ${scrubbing ? 'is-scrubbing' : ''}`}
            role="slider"
            tabIndex={0}
            aria-label="Video progress"
            aria-valuemin={0}
            aria-valuemax={Math.round(duration) || 0}
            aria-valuenow={Math.round(currentTime) || 0}
            aria-valuetext={`${formatTime(currentTime)} of ${formatTime(duration)}`}
            {...scrubHandlers}
            onMouseMove={(e) => { handleProgressHover(e); if (scrubHandlers.onPointerMove) scrubHandlers.onPointerMove(e); }}
            onMouseLeave={() => setHoverTime(null)}
          >
            <div ref={trackRef} className="ccp__progress-track">
              <div className="ccp__progress-buffered" style={{ width: `${bufferedPercent}%` }} />
              <div className="ccp__progress-fill" style={{ width: `${progressPercent}%` }} />
              <div className="ccp__progress-thumb" style={{ left: `${progressPercent}%` }} />
            </div>
            {hoverTime !== null && !scrubbing && (
              <div className="ccp__progress-tooltip" style={{ left: `${hoverPosition}%` }}>
                {formatTime(hoverTime)}
              </div>
            )}
          </div>

          {/* Control row */}
          <div className="ccp__row">
            <div className="ccp__row-left">
              <button type="button" className="ccp__btn" aria-label={playing ? 'Pause' : 'Play'}
                onClick={(e) => { e.stopPropagation(); togglePlay(); }}>
                {playing ? <PauseIcon size={18} /> : <PlayIcon size={18} />}
              </button>
              <div className="ccp__volume">
                <button type="button" className="ccp__btn" aria-label={volume === 0 ? 'Unmute' : 'Mute'}
                  onClick={(e) => { e.stopPropagation(); toggleMute(); }}>
                  <VolumeIcon />
                </button>
                <input
                  type="range" className="ccp__volume-slider" min="0" max="1" step="0.05"
                  value={volume} aria-label="Volume"
                  onChange={(e) => setVolume(parseFloat(e.target.value))}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
              <span className="ccp__time">{formatTime(currentTime)} / {formatTime(duration)}</span>
            </div>
            <div className="ccp__row-right">
              {captions && (
                <button type="button"
                  className={`ccp__btn ccp__btn--cc ${captionsOn ? 'is-active' : ''}`}
                  aria-label={captionsOn ? 'Hide captions' : 'Show captions'}
                  aria-pressed={captionsOn}
                  onClick={(e) => { e.stopPropagation(); setCaptionsOn((on) => !on); }}>
                  <CaptionsIcon />
                </button>
              )}
              <button type="button" className={`ccp__btn ccp__btn--speed ${speed !== 1 ? 'is-active' : ''}`}
                aria-label="Playback speed" title="Playback speed"
                onClick={(e) => { e.stopPropagation(); cycleSpeed(1); }}>
                {speed}×
              </button>
              <button type="button" className="ccp__btn" aria-label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
                onClick={(e) => { e.stopPropagation(); toggleFullscreen(); }}>
                {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export { SPEED_OPTIONS };
export default CCPlayerTheater;
